import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import i18next from 'i18next';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { PersistGate } from 'redux-persist/integration/react';
import { SnackbarProvider } from 'notistack';
import * as Sentry from '@sentry/react';

import { ApolloClient, HttpLink, InMemoryCache, ApolloProvider, from, split } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';

import Notifier from './components/Notifier';
import { DynamicThemeProvider } from './components/DynamicThemeProvider';
import BoeiChatWidget from './components/BoeiChatWidget';
import store, { persistor } from './store';
import router from './layout/router';
import { getMainDefinition } from '@apollo/client/utilities';
import { resetUser } from './store/userSlice';
import { resetFilter, setBookingMode } from './store/filterSlice';
import { resetReportFilters } from './store/reportFiltersSlice';
import { resetTaskSort } from './store/taskSortSlice';
import { EBookingMode } from './types';
import './validation';

// Initialize Sentry
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'development',
  release: process.env.REACT_APP_SENTRY_RELEASE,

  // We're NOT using performance monitoring (only error tracking)
  // tracesSampleRate: 0,

  // No additional integrations needed for error tracking only
});

// Flag to prevent multiple redirects
let isRedirectingToLogin = false;
let isRedirectingToError = false;
const AUTH_BOUNDARY_RESET_KEY = 'authBoundaryResetRequired';

type SentryIdentityState = {
  userId: string | null;
  spaceId: string | null;
};

/**
 * Extracts current user and space IDs from Redux store for Sentry context.
 * Prefers selected space from filter, falls back to user's default space.
 */
const resolveSentryIdentity = (): SentryIdentityState => {
  const state = store.getState() as any;
  const user = state?.user?.value || null;
  const selectedSpaceId = state?.filter?.value?.spaceId ?? null;
  const fallbackSpaceId = user?.space?.id ?? null;

  return {
    userId: user?.id ? String(user.id) : null,
    spaceId: selectedSpaceId ? String(selectedSpaceId) : fallbackSpaceId ? String(fallbackSpaceId) : null,
  };
};

let lastSentryIdentity: SentryIdentityState = {
  userId: null,
  spaceId: null,
};

/**
 * Updates Sentry user context and tags when user or space changes.
 * Only updates if identity actually changed to avoid unnecessary API calls.
 */
const syncSentryIdentityScope = () => {
  const nextIdentity = resolveSentryIdentity();

  if (nextIdentity.userId === lastSentryIdentity.userId && nextIdentity.spaceId === lastSentryIdentity.spaceId) {
    return;
  }

  Sentry.setUser(nextIdentity.userId ? { id: nextIdentity.userId } : null);
  Sentry.setContext(
    'seminargo',
    nextIdentity.userId || nextIdentity.spaceId
      ? {
        ...(nextIdentity.userId ? { userId: nextIdentity.userId } : {}),
        ...(nextIdentity.spaceId ? { spaceId: nextIdentity.spaceId } : {}),
      }
      : null,
  );
  Sentry.setTag('spaceId', nextIdentity.spaceId || 'unknown');

  lastSentryIdentity = nextIdentity;
};

syncSentryIdentityScope();
store.subscribe(syncSentryIdentityScope);

/**
 * Converts unknown error values to readable string format for error reporting.
 * Handles Error objects, strings, and other types gracefully.
 */
const normalizeUnknownError = (value: unknown): string => {
  if (value instanceof Error) {
    return [value.name, value.message, value.stack].filter(Boolean).join('\n');
  }
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/**
 * Redirects to error page with encoded error messages and source URL.
 * Prevents multiple redirects with flags and pathname check.
 */
const isGuestOfferAccessPath = () => window.location.pathname.startsWith('/offers/access/');

const redirectToErrorPage = (messages: string[], source: string) => {
  if (isRedirectingToLogin || isRedirectingToError || window.location.pathname === '/error' || isGuestOfferAccessPath()) {
    return;
  }
  isRedirectingToError = true;

  const encodedErr = encodeURIComponent(JSON.stringify(messages.filter(Boolean)));
  const encodedSource = encodeURIComponent(source || window.location.pathname + window.location.search);

  window.location.assign(`/error?err=${encodedErr}&source=${encodedSource}`);
};

/**
 * Registers global error handlers for unhandled errors and promise rejections.
 * Reports to Sentry and redirects to error page.
 */
const registerGlobalErrorHandlers = () => {
  window.addEventListener('error', event => {
    if (isGuestOfferAccessPath()) {
      console.error('[Guest offer error]', event.error ?? event.message);
      return;
    }
    const details: string[] = [];
    if (event.message) details.push(`WindowError: ${event.message}`);
    if (event.filename) details.push(`File: ${event.filename}:${event.lineno}:${event.colno}`);
    if (event.error) details.push(normalizeUnknownError(event.error));

    const source = window.location.pathname + window.location.search;

    Sentry.captureException(event.error || new Error(event.message || 'Unknown window error'), {
      tags: {
        errorType: 'window-error',
        errorSource: 'global-handler',
      },
      contexts: {
        globalError: {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          source,
        },
      },
    });

    redirectToErrorPage(details.length > 0 ? details : ['Unknown window error'], source);
  });

  window.addEventListener('unhandledrejection', event => {
    if (isGuestOfferAccessPath()) {
      console.error('[Guest offer rejection]', event.reason);
      return;
    }
    const details = [`UnhandledRejection: ${normalizeUnknownError(event.reason)}`];
    const source = window.location.pathname + window.location.search;

    Sentry.captureException(event.reason instanceof Error ? event.reason : new Error(normalizeUnknownError(event.reason)), {
      tags: {
        errorType: 'unhandled-rejection',
        errorSource: 'global-handler',
      },
      contexts: {
        globalRejection: {
          source,
        },
      },
    });

    redirectToErrorPage(details, source);
  });
};

registerGlobalErrorHandlers();

/**
 * Clears all Redux state and persisted storage before auth redirect.
 * Prevents stale filter state from rehydrating after redirect.
 */
const clearSessionBoundaryState = async () => {
  store.dispatch(resetUser());
  store.dispatch(resetFilter());
  store.dispatch(resetReportFilters());
  store.dispatch(resetTaskSort());
  store.dispatch(setBookingMode(EBookingMode.EPILO));
  sessionStorage.removeItem('maintenanceBypass');
  sessionStorage.setItem(AUTH_BOUNDARY_RESET_KEY, '1');

  // Flush and purge persisted store so stale filter cannot rehydrate after redirect.
  try {
    await persistor.flush();
    await persistor.purge();
  } catch (error) {
    console.warn('Failed to purge persisted redux state on auth redirect', error);
  }
};

/**
 * Redirects to login page after clearing all application state.
 * Prevents redirect loop if already on auth pages.
 */
const redirectToLogin = () => {
  if (isRedirectingToLogin) {
    return;
  }

  const isAuthPage =
    window.location.pathname === '/login' ||
    window.location.pathname.startsWith('/forgotpassword') ||
    window.location.pathname.startsWith('/offers/access/');
  isRedirectingToLogin = true;
  clearSessionBoundaryState()
    .then(() => {
      return client.clearStore();
    })
    .catch(error => {
      console.warn('Failed to clear Apollo store on auth redirect', error);
    })
    .finally(() => {
      if (isAuthPage) {
        isRedirectingToLogin = false;
        return;
      }
      const from = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.href = `/login?from=${encodeURIComponent(from)}`;
    });
};

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, Extensions: ${JSON.stringify(extensions, null, 2)}`);

      // Handle BAD_AUTH errors - redirect to login (don't report to Sentry)
      if (extensions?.code === 'BAD_AUTH' || extensions?.code === 'INVALID_OFFER_TOKEN' || extensions?.code === 'LOGGED_IN_NO_OFFER_ACCESS') {
        if (window.location.pathname.startsWith('/offers/access/')) {
          return;
        }
        console.log('[Auth error]: Session expired or invalid, redirecting to login');
        redirectToLogin();
      } else {
        // Report other GraphQL errors to Sentry
        const errorContext: Sentry.CaptureContext = {
          tags: {
            errorType: 'graphql',
            errorCode: String(extensions?.code || 'unknown'),
          },
          contexts: {
            graphql: {
              message,
              path: path as any,
              locations: locations as any,
            },
          },
        };
        Sentry.captureException(new Error(message), errorContext);
      }
    });
  }
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
    // Report network errors to Sentry (except auth errors)
    if ((networkError as any).statusCode !== 401) {
      const errorContext: Sentry.CaptureContext = {
        tags: {
          errorType: 'network',
        },
      };
      Sentry.captureException(networkError, errorContext);
    }
  }

  if (networkError && (networkError as any).statusCode === 401) {
    if (!window.location.pathname.startsWith('/offers/access/')) {
      redirectToLogin();
    }
  }
});
// Retries failed queries (not mutations) with exponential backoff, excluding auth errors and aborted requests
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 1500,
    jitter: true,
  },
  attempts: {
    max: 2,
    retryIf: (error, operation) => {
      if (!error || isRedirectingToLogin) return false;

      const definition = getMainDefinition(operation.query);
      const isMutation = definition.kind === 'OperationDefinition' && definition.operation === 'mutation';
      if (isMutation) return false;

      const anyError = error as any;
      const statusCode = anyError?.statusCode ?? anyError?.status;
      if (statusCode === 401 || statusCode === 403) return false;

      const errorName = `${anyError?.name || ''}`.toLowerCase();
      if (errorName.includes('abort')) return false;

      return true;
    },
  },
});

const httpLinkUrl = process.env.REACT_APP_GRAPHQL_ENDPOINT || '/backend/graphql';
const wsLinkUrl = httpLinkUrl.replace('http:', 'ws:').replace('https:', 'wss:');

console.log('httpLinkUrl', httpLinkUrl);
console.log('wsLinkUrl', wsLinkUrl);

const httpLink = new HttpLink({
  uri: httpLinkUrl,
  credentials: 'include',
});
const wsLink = new GraphQLWsLink(
  createClient({
    url: wsLinkUrl,
    connectionParams: () => {
      return {
        headers: {
          Cookie: document.cookie,
        },
      };
    },
    // Limit reconnection attempts to avoid hammering the server when unauthenticated
    retryAttempts: 3,
    shouldRetry: () => {
      // Only retry if we're not in the middle of redirecting to login
      return !isRedirectingToLogin;
    },
    on: {
      connected: () => {
        console.log('WSLink Connected!', wsLinkUrl);
      },
      closed: (event) => {
        console.log('WSLink Closed!', wsLinkUrl, event);
        // If connection was rejected (code 4401 or similar auth error), redirect to login
        if (event && typeof event === 'object' && 'code' in event) {
          const code = (event as any).code;
          // 4401 = Unauthorized, 4403 = Forbidden (common WS close codes for auth issues)
          if (code === 4401 || code === 4403 || code === 1008) {
            console.log('[WS Auth error]: Connection rejected, redirecting to login');
            redirectToLogin();
          }
        }
      },
      error: error => {
        console.warn('WSLink error!', wsLinkUrl, error);
      },
    },
  }),
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink,
);

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: from([errorLink, retryLink, splitLink]),
});

const rootElement = document.getElementById('root');
const root = ReactDOM.createRoot(rootElement!);

function LanguageAwareRouter() {
  const [languageVersion, setLanguageVersion] = React.useState(0);

  React.useEffect(() => {
    const rerenderOnLanguageChange = () => setLanguageVersion(v => v + 1);
    i18next.on('languageChanged', rerenderOnLanguageChange);
    return () => i18next.off('languageChanged', rerenderOnLanguageChange);
  }, []);

  return <RouterProvider key={`bo-router-${languageVersion}`} router={router} />;
}

root.render(
  <Sentry.ErrorBoundary fallback={<div style={{ padding: '20px', textAlign: 'center' }}>
    <h1>{i18next.t('error-boundary-title')}</h1>
    <p>{i18next.t('error-boundary-description')}</p>
    <button onClick={() => window.location.reload()}>{i18next.t('error-boundary-reload')}</button>
  </div>}>
    <ApolloProvider client={client}>
      <Provider store={store}>
        <PersistGate persistor={persistor}>
          <SnackbarProvider maxSnack={3}>
            <DynamicThemeProvider>
              {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
              <CssBaseline />
              <Notifier />
              <BoeiChatWidget />
              <LanguageAwareRouter />
            </DynamicThemeProvider>
          </SnackbarProvider>
        </PersistGate>
      </Provider>
    </ApolloProvider>
  </Sentry.ErrorBoundary>,
);
